import React from "react";
import styled from "styled-components";
import { narrowScreenMediaQuery } from "../constants/styles";
import { notif_icon_src } from "../images/index";
import { apiGet } from "../api";
import useDropdown from "./useDropdown";

// Notifications poll every 60s. For logged-out users the parent Header
// doesn't render us, so the endpoint is never hit anonymously.
const useNotifications = () => {
  const [notifications, setNotifications] = React.useState([]);

  const refresh = React.useCallback(async () => {
    const { parsedResponse } = await apiGet("/api/v1/users/notifications");
    if (Array.isArray(parsedResponse)) setNotifications(parsedResponse);
  }, []);

  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  return { notifications };
};

function Notification() {
  const { notifications } = useNotifications();
  const { open, toggle, close, rootRef } = useDropdown();

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Notifikasi"
      >
        <IconImg src={notif_icon_src} alt="" />
        {notifications.length > 0 && <UnreadDot aria-hidden="true" />}
        <Chevron $open={open} aria-hidden="true" />
      </Trigger>

      {open && (
        <Panel role="menu">
          <PanelHeader>Notifikasi</PanelHeader>
          <PanelBody>
            {notifications.length === 0 ? (
              <EmptyMessage>Belum ada notifikasi</EmptyMessage>
            ) : (
              notifications.map(({ id, url, time_text, title, content }) => (
                <Item
                  key={id}
                  href={url}
                  {...(!String(url || "").includes("learning.taxsam.co") && {
                    target: "_blank",
                    rel: "noreferrer",
                  })}
                  role="menuitem"
                  onClick={close}
                >
                  <ItemIcon>
                    <img src={notif_icon_src} alt="" />
                  </ItemIcon>
                  <ItemBody>
                    <ItemTime>{time_text}</ItemTime>
                    <ItemTitle>{title}</ItemTitle>
                    <ItemDesc>{content}</ItemDesc>
                  </ItemBody>
                </Item>
              ))
            )}
          </PanelBody>
        </Panel>
      )}
    </Root>
  );
}
export default React.memo(Notification);

// ---- styles -------------------------------------------------------------

const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

const Trigger = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  padding: 8px;
  position: relative;
  transition: background 120ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  [data-theme="dark"] &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

// Source SVG has hardcoded white fill — tint to dark so it's visible on cream.
const IconImg = styled.img`
  width: 22px;
  height: 22px;
  display: block;
  filter: brightness(0) saturate(100%);
  opacity: 0.78;

  [data-theme="dark"] & {
    filter: brightness(0) saturate(100%) invert(1);
    opacity: 0.9;
  }
`;

const UnreadDot = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #d85907;
  border-radius: 999px;
  box-shadow: 0 0 0 2px #fef5e0;

  [data-theme="dark"] & {
    box-shadow: 0 0 0 2px #17171e;
  }
`;

const Chevron = styled.span`
  display: inline-block;
  margin-left: 4px;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid currentColor;
  color: #6b7280;
  transition: transform 180ms ease;
  transform: ${({ $open }) => ($open ? "rotate(-180deg)" : "none")};

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Panel = styled.div`
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  color: #1f2937;
  cursor: default;
  display: flex;
  flex-direction: column;
  max-height: 500px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 340px;
  max-width: calc(100vw - 24px);
  z-index: 50;
  text-align: left;
  animation: fadeIn 140ms ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* On narrow screens the trigger sits mid-header (with burger to its right),
   * so anchoring the panel to the trigger's right edge causes the left edge
   * to overflow the viewport. Switch to viewport-fixed anchoring. */
  @media ${narrowScreenMediaQuery} {
    position: fixed;
    top: 66px;
    right: 10px;
    left: auto;
    width: 340px;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 80px);
  }

  [data-theme="dark"] & {
    background-color: #17171e;
    border-color: rgba(255, 255, 255, 0.08);
    color: #f3f4f6;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  }
`;

const PanelHeader = styled.div`
  font-weight: 700;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;

  [data-theme="dark"] & {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

const PanelBody = styled.div`
  overflow-y: auto;
  max-height: 440px;
  padding: 4px 0;
`;

const EmptyMessage = styled.div`
  font-size: 13px;
  color: #6b7280;
  padding: 24px 16px;
  text-align: center;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Item = styled.a`
  align-items: flex-start;
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 10px 16px;
  text-align: left;
  text-decoration: none;
  transition: background 120ms ease;

  &:hover {
    background: #f9fafb;
    color: inherit;
    text-decoration: none;
  }

  [data-theme="dark"] &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const ItemIcon = styled.div`
  flex-shrink: 0;
  padding-top: 2px;

  img {
    width: 16px;
    height: 16px;
    display: block;
    filter: brightness(0) saturate(100%);
    opacity: 0.55;
  }

  [data-theme="dark"] & img {
    filter: brightness(0) saturate(100%) invert(1);
    opacity: 0.55;
  }
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  text-align: left;
`;

const ItemTime = styled.div`
  color: #9ca3af;
  font-size: 11px;
  margin-bottom: 4px;

  [data-theme="dark"] & {
    color: #6b7280;
  }
`;

const ItemTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  line-height: 1.4;
  overflow-wrap: anywhere;

  ${Item}:hover & {
    color: #d85907;
  }
`;

const ItemDesc = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  overflow-wrap: anywhere;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;
